import { useState, useRef, useMemo, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getTranscribedText } from "api/deepgram";
import { getUsageData } from "api/usage";
import useCreateNotes from "zustand-store/create-notes";
import { getNotesWithMoreTemplates } from "api/deepgram";
import ScaleBarrierLoader from "components/scale-barrier-loader";
import { ReactComponent as CheckFilled } from "assets/svgs/check-filled.svg";
import { ReactComponent as CheckUnfilled } from "assets/svgs/check-unfilled.svg";
import { getAllCustomTemplates, getAllTemplates, getAllTemplatesUserIdSortByProfession, getFullTemplateDetails, saveTemplate, updateTemplate } from "api/templates";
import { getUserByCogId } from "api/user";
import ShimmerLoader from "components/shimmer-loader";
import { getAllTopicsByTemplateId } from "api/topic";
import { Sparkle, SquarePen, Plus, Pencil, Trash2, PlusIcon, Star, ChevronDown, ChevronUp, Settings2, Save, Search } from 'lucide-react';
import { useFieldArray, useForm } from "react-hook-form";
import { ReactComponent as StarsIcon } from "assets/svgs/stars.svg";
import useMicSettings from "zustand-store/mic";
import useCustomAudioRecorder from "utils/hooks/use-custom-audio-recorder";
import ReactDOM from "react-dom";
import CloseButton from "components/ui/close-button";
import InputFieldLabel from "components/input-field-label";
import { toast } from "react-toastify";
import errorCopy from "utils/error-copy";
import Modal from "components/modal";
import DeleteConfirmation from "sections/modal/DeleteConfirmation";
import { createFavoriteTemplate, deleteFavoriteTemplate } from "api/favorite";
import Spinner from "components/Spinner";
import UseExistingTemplateModal from "components/useExistingTemplateModal";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SortableTopicRow from "components/SortableTopicRow";

const AddMoreTemplates = ({ allTemplates, setActive, setChangesMade, setIsTranscriptEdited, setLoadingTemplates }) => {
  const {
    currentSession,
    transcribedText,
    selectedTemplates,
    setGeneratedNotes,
    setSelectedTemplates,
    currentPatient,
    setTranscribedText,
    setShowSaveSession,
    setSessionDuration,
    setCurrentAudioBlob,
  } = useCreateNotes();

  const { inputDevice } = useMicSettings();

  const { data: userData, isLoading } = useQuery("userProfile", getUserByCogId, {});
  const { data: currentUser } = useQuery("currentUser", getUserByCogId);
  const userId = currentUser?.cognitoId;

  const inputValues = ["Paragraph", "Points"];

  const {
    isPaused,
    audioBlob,
    canvasRef,
    isStopped,
    isRecording,
    audioDuration,
    stopRecording,
    startRecording,
  } = useCustomAudioRecorder(inputDevice?.deviceId);

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [moreTemplates, setMoreTemplates] = useState(selectedTemplates);
  const [showChooseTemplateModal, setShowChooseTemplateModal] = useState(false);
  const [selectedSection, setSelectedSection] = useState(0);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewData, setPreviewData] = useState({});
  const [previewTopics, setPreviewTopics] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [showCustomTemplate, setShowCustomTemplate] = useState(false);
  const [showUseTemplateModal, setShowUseTemplateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [noOfTopics, setNoOfTopics] = useState(1);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [notShowTemplateDescription, setNotShowTemplateDescription] = useState([]);
  const [loadingFavTemplate, setLoadingFavTemplate] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [expandAllTopics, setExpandAllTopics] = useState(false);

  // Reset the expand/collapse state whenever the create/edit modal closes.
  useEffect(() => {
    if (!showCustomTemplate) setExpandAllTopics(false);
  }, [showCustomTemplate]);

  const handleRemoveTopic = (ind) => {
    if (fields.length === 1) {
      toast.error("At least one topic required");
      return;
    }

    remove(ind);
    setNoOfTopics(prev => prev - 1);
  };

  const handlePreviewButtonClick = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    const { templateId, templateName, rules, description, topics } = getValues();

    const formattedTopics = topics.map((topic, ind) => ({
      name: topic.name,
      description: topic.prompt,
      id: ind,
    }))

    const template = {
      id: templateId,
      name: templateName,
      description,
      rules,
      topics: formattedTopics,
    };

    handlePreviewClick(template);
  };

  const handleAddTopics = () => {
    if (fields.length >= 10) {
      toast.error("You cannot add more than 10 topics");
      return;
    }

    append({
      name: "",
      summarizationType: "Points",
      prompt: ""
    });
  };

  const closeModal = () => {
    setShowCustomTemplate(false);
    setIsUpdating(false);
    reset({
      templateName: "",
      description: "",
      topics: [
        { summarizationType: "Points", }
      ]
    })
    setNoOfTopics(1);
    setShowDetails(false);
  }

  const { mutate: updateCustomTemplate, isLoading: updateTemplateLoading } = useMutation(
    (data) => updateTemplate(data), {
    onSuccess: () => {
      reset({
        templateName: "",
        description: "",
        topics: [{ summarizationType: "Points" }]
      })
      setShowCustomTemplate(false);
      toast.success("Template Updated");
      queryClient.invalidateQueries('customTemplates');
    },
    onError: () => {
      setShowCustomTemplate(false);
      toast.error("Template update failed.")
    }
  })

  const handleUpdateTemplate = async (data) => {
    const isValid = await trigger();
    if (!isValid) return;

    const topicNames = data.topics.map(t => t.name?.trim().toLowerCase());
    const nameSet = new Set(topicNames);

    if (nameSet.size !== topicNames.length) {
      toast.error('Topic names must be unique');
      return;
    }

    const topicsWithIndex = data.topics.map((t, i) => ({
      ...t,
      summarizationType: t.summarizationType === "Points" ? "bullet" : t.summarizationType,
      position: i + 1
    }));

    const finalData = {
      ...data,
      topics: topicsWithIndex
    };

    const trimmedData = deepTrim(finalData);
    updateCustomTemplate(trimmedData);
  };

  const { mutate: saveCustomTemplate, isLoading: saveTemplateLoading } = useMutation(saveTemplate, {
    onSuccess: () => {
      reset({
        templateName: "",
        description: "",
        topics: [{ summarizationType: "Points" }]
      })
      setShowCustomTemplate(false);
      toast.success("Template Created");
      queryClient.invalidateQueries('customTemplates');
    },
    onError: () => {
      setShowCustomTemplate(false);
      toast.error("Failed to create template");
    }
  })

  const deepTrim = (value) => {
    if (typeof value === "string") {
      return value.trim();
    }

    if (Array.isArray(value)) {
      return value.map(item => deepTrim(item));
    }

    if (typeof value === "object" && value !== null) {
      const trimmedObj = {};
      Object.keys(value).forEach(key => {
        trimmedObj[key] = deepTrim(value[key]);
      });
      return trimmedObj;
    }

    return value;
  };

  const handleSaveTemplate = async (data) => {
    const isValid = await trigger();
    if (!isValid) {
      toast.error("Please fill all required fields before updating");
      return;
    }

    const topicNames = data.topics.map(t => t?.name?.trim().toLowerCase());
    const nameSet = new Set(topicNames);

    if (topicNames.length !== nameSet.size) {
      toast.error('Topic names must be unique');
      return;
    }

    const topicsWithIndex = data.topics.map((t, i) => ({
      ...t,
      summarizationType: t.summarizationType === "Points" ? "bullet" : t.summarizationType,
      position: i + 1
    }));

    const finalData = {
      ...data,
      topics: topicsWithIndex
    };

    const trimmedData = deepTrim(finalData);
    saveCustomTemplate(trimmedData);
  };

  const handlePreviewClick = async (template) => {
    setPreviewTemplate(template);

    if (template.topics && template.topics.length > 0) {
      const filtered = sortTopicsByPosition(template.topics);
      setPreviewTopics(filtered);
      setPreviewData(prev => ({
        ...prev,
        [template.id]: filtered
      }));
      return;
    }

    if (previewData[template.id]) {
      setPreviewTopics(previewData[template.id]);
      return;
    }

    setIsPreviewLoading(true);
    try {
      const topics = await getAllTopicsByTemplateId(template.id);
      const filtered = sortTopicsByPosition(topics);
      setPreviewData(prev => ({
        ...prev,
        [template.id]: filtered
      }));
      setPreviewTopics(filtered);
    } catch (error) {
      console.error('Failed to fetch template topics:', error);
      setPreviewTopics([]);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!updateTemplateLoading)
      setShowDetails(false);
  }, [updateTemplateLoading])

  useEffect(() => {
    if (!saveTemplateLoading)
      setShowDetails(false);
  }, [saveTemplateLoading])

  const handleSubmitNewTemplates = async () => {
    try {
      setIsTranscriptEdited(false);
      setShowChooseTemplateModal(false);
      setLoading(true);
      setLoadingTemplates(true);

      if (moreTemplates.length === 0) {
        handleUnselectedTemplates([]);
        return;
      }

      setSelectedTemplates(moreTemplates);

      const response = await getNotesWithMoreTemplates({
        selectedTemplates: moreTemplates,
        transcribedText,
        sessionId: currentSession.id,
      });

      handleUnselectedTemplates(response.outputs);

    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
      setLoadingTemplates(false);
    }
  };

  const sections = [
    { id: 1, name: 'Nirva Templates' },
    { id: 2, name: 'My Favorites' },
    { id: 3, name: 'Custom Templates' },
  ]

  const sortTopicsByPosition = (topics) => {
    if (!topics) return [];
    return [...topics]
      .filter(t => !t.isDeleted)
      .sort((a, b) => (a.position) - (b.position));
  };

  const customTemplateUpdation = async (e, templateId) => {
    e.stopPropagation();

    const templateData = await getFullTemplateDetails(templateId);

    setNoOfTopics(templateData.topics.length)
    reset({
      templateId: templateData.id,
      templateName: templateData.name,
      description: templateData.description,
      rules: templateData.rules,
      topics: sortTopicsByPosition(templateData.topics).map(topic => ({
        id: topic.id,
        name: topic.name,
        summarizationType: topic.type === "bullet" ? "Points" : topic.type,
        prompt: topic.description,
      }))
    })
    setShowCustomTemplate(true);
    setShowUseTemplateModal(false);
  }

  const closePreview = () => {
    setNotShowTemplateDescription([]);
    setPreviewTemplate(null);
    setPreviewTopics([]);
  };

  const {
    refetch,
    data: outputTemplates = [],
    error: templatesFetchError,
    isLoading: loadingNirvaTemplates
  } = useQuery(
    ["templatesPersonalized", userId],
    () => {
      if (userId) {
        return getAllTemplatesUserIdSortByProfession(userId);
      } else {
        return getAllTemplates();
      }
    },
    {
      enabled: false,
    }
  );

  const { data: customTemplatesData = [], isLoading: customTemplatesLoading } = useQuery('customTemplates',
    getAllCustomTemplates,
  );

  const filteredTemplates = allTemplates?.filter(({ name }) =>
    name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const filteredCustomTemplates = customTemplatesData?.filter(({ name }) =>
    name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const nirvaAndCustomTemplates = [...allTemplates, ...customTemplatesData];

  const filteredAllTemplates = nirvaAndCustomTemplates.filter(({ name }) =>
    name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const { recommendedTemplates, otherTemplates } = useMemo(() => {
    if (!filteredTemplates) return { recommendedTemplates: [], otherTemplates: [] };

    const recommended = [];
    const other = [];

    filteredTemplates.forEach(template => {
      if (template.medicalProfession === "General medicine" || template.recommend === 1) {
        recommended.push(template);
      } else {
        other.push(template);
      }
    });

    return { recommendedTemplates: recommended, otherTemplates: other };
  }, [filteredTemplates]);

  const cannotGenerateNotes =
    loading || templatesFetchError || (moreTemplates || []).length === 0;

  const { control, register, reset, trigger, handleSubmit, getValues, setValue, formState: { errors } }
    = useForm({
      defaultValues: {
        templateName: "",
        description: "",
        rules: "",
        topics: [{
          summarizationType: 'Points'
        }]
      }
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "topics"
  });

  const initialSelectedTemplatesRef = useRef(selectedTemplates);

  const { data: userUsageData } = useQuery("userUsage", getUsageData, {
    enabled: false,
  });

  const queryClient = useQueryClient();

  const getNewlySelectedTemplates = () => {
    return moreTemplates.filter(
      (templateId) => !initialSelectedTemplatesRef.current.includes(templateId)
    );
  };

  const { mutateAsync: createFavTemplate, isLoading: createFavTemplateLoading } = useMutation(
    (data) => createFavoriteTemplate(data), {
    onSuccess: () => {
      toast.success('Added to favorite templates');
      // Create Notes builds its tab strip from this query; without the
      // invalidation a newly favourited template did not appear until reload.
      queryClient.invalidateQueries('getAllFavorites');
    },
    onError: () => {
      toast.error("Something went wrong");
    }
  })

  const { mutateAsync: deleteFavTemplate, isLoading: deleteFavTemplateLoading } = useMutation(
    (data) => deleteFavoriteTemplate(data), {
    onSuccess: () => {
      toast.success('Removed from favorite templates');
      // Create Notes builds its tab strip from this query; without the
      // invalidation a newly favourited template did not appear until reload.
      queryClient.invalidateQueries('getAllFavorites');
    },
    onError: () => {
      toast.error("Something went wrong");
    }
  })

  const toggleFavoriteTemplate = async (e, template) => {
    e.stopPropagation();
    const isFavorite = template.isFavorite;

    try {
      if (!isFavorite)
        createFavTemplate({ templateId: template.id })
      else
        deleteFavTemplate({ templateId: template.id })

      queryClient.setQueriesData(['templates', userId], (oldData = []) => {
        return oldData.map((t) => {
          if (t.id === template.id) {
            return {
              ...t,
              isFavorite: !t.isFavorite
            }
          }
          else
            return t;
        })
      })

    } catch (error) {
      toast.error(errorCopy(error.message));
      console.log(error);
    }
  }

  const toggleFavoriteCustomTemplate = async (e, template) => {
    e.stopPropagation();
    const isFavorite = template.isFavorite;

    try {
      if (!isFavorite)
        await createFavTemplate({ templateId: template.id })
      else
        await deleteFavTemplate({ templateId: template.id })

      queryClient.setQueriesData(['customTemplates'], (oldData = []) => {
        return oldData.map((t) => {
          if (t.id === template.id) {
            return {
              ...t,
              isFavorite: !t.isFavorite
            }
          }
          else
            return t;
        })
      })

    } catch (error) {
      toast.error(errorCopy(error.message));
      console.log(error);
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);

    const newArr = arrayMove(getValues("topics"), oldIndex, newIndex);
    setValue("topics", newArr);
  };

  const TemplateItem = ({ template, section }) => {
    const { id, name } = template;
    const isSelected = moreTemplates.includes(id);
    const cursorStyles = "hover:bg-shade-50 cursor-pointer";

    return (
      <li key={`${section}-${id}`} className="border-b border-rule last:border-b-0 transition-all duration-200">
        <div
          onClick={() => handleSelectMoreTemplates(template)}
          className={`px-4 flex items-center gap-3 py-3 transition-all duration-200 ${cursorStyles}`}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
              {isSelected ? <CheckFilled /> : <CheckUnfilled />}
            </div>
            <span className="flex-1 text-sm font-medium text-ink truncate">{name || ""}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePreviewClick(template);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent bg-shade-50 hover:text-accent rounded-ns transition-all duration-200 transform "
            >
              <Sparkle size={14} />
              Preview
            </button>
            <button
              className="p-2 hover:bg-shade-50 rounded-ns transition-all duration-200 transform "
              disabled={(createFavTemplateLoading || deleteFavTemplateLoading) && (template.id === loadingFavTemplate)}
              onClick={(e) => {
                template.userId === null ? toggleFavoriteTemplate(e, template) : toggleFavoriteCustomTemplate(e, template);
                setLoadingFavTemplate(template.id);
              }}
            >
              {(createFavTemplateLoading || deleteFavTemplateLoading) && template.id === loadingFavTemplate ? (
                <Spinner size={18} />
              ) : (
                <Star 
                  size={20} 
                  fill={template.isFavorite ? 'var(--ns-warn)' : 'none'} 
                  stroke="currentColor"
                  strokeWidth={2}
                />
              )}
            </button>
          </div>
        </div>
      </li>
    );
  };

  useEffect(() => {
    setMoreTemplates(selectedTemplates);
  }, [selectedTemplates])

  const hasChanges = () => {
    const initial = [...initialSelectedTemplatesRef.current].sort();
    const current = [...moreTemplates].sort();
    return JSON.stringify(initial) !== JSON.stringify(current);
  };

  const newlySelectedTemplates = getNewlySelectedTemplates();
  const isDisabled = !hasChanges();

  const handleSelectMoreTemplates = (template) => {
    setMoreTemplates((prev) => {
      const prevTemplates = [...prev];
      if (prevTemplates.includes(template.id)) {
        return prevTemplates.filter((ele) => ele !== template.id);
      }
      return [...prevTemplates, template.id];
    });
  };

  useEffect(() => {
    if (showChooseTemplateModal) {
      if (customTemplatesData.length > 0) {
        setSelectedSection(3);
      }
      else if (favoriteTemplates.length > 0) {
        setSelectedSection(2);
      }
      else {
        setSelectedSection(1);
      }
    }
  }, [showChooseTemplateModal]);

  const handleUnselectedTemplates = (newNotes) => {
    const { generatedNotes } = useCreateNotes.getState();

    const existingNotesToKeep = generatedNotes.filter(note =>
      moreTemplates.includes(note.templateId)
    );

    const allNotes = [...existingNotesToKeep, ...newNotes];

    setSelectedTemplates([...moreTemplates]);
    setGeneratedNotes([...newNotes]);

    if (allNotes.length > 0) {
      if (newlySelectedTemplates.length > 0) {
        setActive(newlySelectedTemplates[0]);
      } else {
        setActive(allNotes[0].templateId);
      }
    }

    setChangesMade(true);
    initialSelectedTemplatesRef.current = [...moreTemplates];
  };

  const getButtonTextClass = () => {
    if (loading) return "opacity-0";
    if (cannotGenerateNotes) return "opacity-20";
    return "opacity-100";
  };

  const allFavTemplates = [...allTemplates, ...customTemplatesData];

  const favoriteTemplates = allFavTemplates?.filter(template =>
    template.isFavorite === true &&
    template.name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <>
      <div className="relative">
        {/* Mirrors the Insights control in the Transcript header (save-session.jsx):
            same h-9, same padding, same type. The old 32px filled AddIcon was
            10px taller than its opposite number, which pushed the two panel
            rules out of line. */}
        <button
          type="button"
          onClick={() => setShowChooseTemplateModal(prev => !prev)}
          className="flex h-9 flex-shrink-0 items-center gap-1.5 rounded-ns-sm px-2.5 font-body text-ui-xs font-medium text-muted transition-colors duration-ui ease-ui hover:bg-shade-50 hover:text-ink active:scale-[.98]"
        >
          <Plus size={16} />
          <span>Add more</span>
        </button>
      </div>

      {loading && <ScaleBarrierLoader />}

      {/* Choose template Modal */}
      {showChooseTemplateModal && (
        <>
          {ReactDOM.createPortal(
            <div className={`fixed inset-0 bg-ink/70 backdrop-blur-sm z-[70] animate-fade-in ${showUseTemplateModal ? "opacity-20 pointer-events-none" : ""}`} />,
            document.body
          )}

          {ReactDOM.createPortal(
            <div className={`fixed inset-0 z-[80] flex items-center justify-center p-4 pointer-events-none ${showUseTemplateModal ? "opacity-20" : ""}`}>
            <div
              className={`pointer-events-auto w-[90vw] sm:w-[70vw] max-w-[650px] rounded-ns flex flex-col overflow-hidden shadow-pop animate-scale-in ${showUseTemplateModal ? "pointer-events-none" : ""}`}
            >
              <div className="relative w-full flex flex-col max-h-[85vh]">
                <div className="flex-1 overflow-hidden flex flex-col">
                  <div className="bg-shade-50 rounded-t-ns border-2 border-rule border-b-0 px-6 py-5 flex flex-col">
                    <div className="flex justify-between items-center w-full mb-4">
                      <h2 className="text-xl font-semibold text-ink">
                        Choose Templates
                      </h2>
                      <CloseButton
                        onClick={() => {
                          setShowChooseTemplateModal(false);
                          setMoreTemplates(selectedTemplates)
                        }}
                      />
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-2 mb-4">
                      {/* The selected tab used to be `bg-shade-50`, which is the
                          exact colour of the modal header behind it — so the open
                          tab read as a hole and the closed ones, being white with
                          borders, looked like the buttons. The selection was
                          effectively inverted.

                          accent-tint now marks the open tab, matching the panel
                          tabs on Create Notes so the two tab strips in this
                          product mean the same thing. */}
                      {sections.map((section) => {
                        const isSelected = selectedSection === section.id;
                        return (
                          <button
                            key={section.id}
                            type="button"
                            role="tab"
                            aria-selected={isSelected}
                            onClick={() => setSelectedSection(section.id)}
                            className={`flex-1 rounded-ns border border-solid px-4 py-2.5 font-body text-ui-sm transition-colors duration-ui ease-ui ${
                              isSelected
                                ? 'border-accent/30 bg-accent-tint font-semibold text-accent'
                                : 'border-rule bg-white text-muted hover:border-rule-strong hover:bg-shade-50 hover:text-ink'
                            }`}
                          >
                            {section.name}
                          </button>
                        );
                      })}
                    </div>

                    {/* Search Bar */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted" />
                      <input
                        type="text"
                        placeholder="Search templates..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 text-sm border border-rule-strong rounded-ns focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  {/* Content Area */}
                  <div className="flex-1 overflow-y-auto bg-white border-2 border-rule border-t-0">
                    {selectedSection === 1 && (
                      <div className="flex flex-col">
                        {loadingNirvaTemplates ? (
                          <div className="p-8">
                            <ShimmerLoader className="w-full h-64" />
                          </div>
                        ) : (
                          <>
                            {recommendedTemplates.length > 0 && (
                              <div>
                                <ul>
                                  {recommendedTemplates.map((template, index) => (
                                    <div
                                      key={`recommended-${template.id}`}
                                      className="animate-fade-in"
                                      style={{ animationDelay: `${index * 50}ms` }}
                                    >
                                      <TemplateItem template={template} section="recommended" />
                                    </div>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {otherTemplates.length > 0 && (
                              <div>
                                <h3 className="px-4 py-3 bg-shade-50 text-ink font-semibold text-sm border-b border-rule">
                                  Other Templates
                                </h3>
                                <ul>
                                  {otherTemplates.map((template, index) => (
                                    <div
                                      key={`other-${template.id}`}
                                      className="animate-fade-in"
                                      style={{ animationDelay: `${index * 50}ms` }}
                                    >
                                      <TemplateItem template={template} section="other" />
                                    </div>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {!recommendedTemplates.length && !otherTemplates.length && (
                              <div className="px-4 py-12 text-center text-muted">
                                No templates found
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {selectedSection === 2 && (
                      <div className="flex flex-col">
                        {loadingNirvaTemplates ? (
                          <div className="p-8">
                            <ShimmerLoader className="w-full h-64" />
                          </div>
                        ) : (
                          <>
                            {favoriteTemplates.length > 0 ? (
                              <ul>
                                {favoriteTemplates.map((template, index) => (
                                  <div
                                    key={`favorite-${template.id}`}
                                    className="animate-fade-in"
                                    style={{ animationDelay: `${index * 50}ms` }}
                                  >
                                    <TemplateItem template={template} section="favorite" />
                                  </div>
                                ))}
                              </ul>
                            ) : (
                              <div className="px-4 py-12 text-center text-muted">
                                No favourites yet.
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {selectedSection === 3 && (
                      <div className="flex flex-col">
                        {customTemplatesLoading ? (
                          <div className="p-8">
                            <ShimmerLoader className="w-full h-64" />
                          </div>
                        ) : customTemplatesData.length === 0 ? (
                          <div className="px-4 py-12 text-center">
                            <p className="text-muted mb-6">No templates of your own yet.</p>
                            <div className="flex flex-col gap-3 items-center">
                              <button
                                onClick={() => setShowUseTemplateModal(true)}
                                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-accent rounded-ns  hover:shadow-pop transition-all duration-200 transform hover:bg-deep"
                              >
                                <SquarePen size={18} />
                                Use Existing Template
                              </button>
                              <button
                                onClick={() => {
                                  setShowCustomTemplate(true);
                                  setIsUpdating(false);
                                }}
                                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-accent rounded-ns  hover:shadow-pop transition-all duration-200 transform "
                              >
                                <Plus size={18} strokeWidth={3} />
                                Create New Template
                              </button>
                            </div>
                          </div>
                        ) : filteredCustomTemplates.length === 0 ? (
                          <div className="px-4 py-12 text-center text-muted">
                            No templates found
                          </div>
                        ) : (
                          <ul>
                            {filteredCustomTemplates.map((customTemplate, index) => (
                              <li
                                key={customTemplate.id}
                                className="border-b border-rule last:border-b-0 animate-fade-in"
                                style={{ animationDelay: `${index * 50}ms` }}
                              >
                                <div
                                  onClick={() => handleSelectMoreTemplates(customTemplate)}
                                  className="px-4 py-3 hover:bg-shade-50 cursor-pointer transition-all duration-200"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0">
                                      {moreTemplates.includes(customTemplate.id) ? (
                                        <CheckFilled />
                                      ) : (
                                        <CheckUnfilled />
                                      )}
                                    </div>
                                    <p className="flex-1 text-sm font-medium text-ink">{customTemplate.name}</p>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handlePreviewClick(customTemplate);
                                        }}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent bg-shade-50 hover:text-accent rounded-ns transition-all duration-200"
                                      >
                                        <Sparkle size={14} />
                                        Preview
                                      </button>
                                      <button
                                        disabled={(createFavTemplateLoading || deleteFavTemplateLoading) && (customTemplate.id === loadingFavTemplate)}
                                        onClick={(e) => {
                                          toggleFavoriteCustomTemplate(e, customTemplate);
                                          setLoadingFavTemplate(customTemplate.id);
                                        }}
                                        className="p-2 hover:bg-shade-50 rounded-ns transition-all duration-200 transform "
                                      >
                                        {(createFavTemplateLoading || deleteFavTemplateLoading) && customTemplate.id === loadingFavTemplate ? (
                                          <Spinner size={18} />
                                        ) : (
                                          <Star
                                            size={20}
                                            fill={customTemplate.isFavorite ? 'var(--ns-warn)' : 'none'}
                                            stroke="currentColor"
                                            strokeWidth={2}
                                          />
                                        )}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          setIsUpdating(true);
                                          customTemplateUpdation(e, customTemplate.id);
                                        }}
                                        className="p-2 text-muted hover:text-accent hover:bg-shade-50 rounded-ns transition-all duration-200"
                                      >
                                        <Pencil size={18} />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowDeleteModal({
                                            templateId: customTemplate.id,
                                            templateName: customTemplate.name,
                                          });
                                        }}
                                        className="p-2 text-muted hover:text-danger hover:bg-danger/10 rounded-ns transition-all duration-200"
                                      >
                                        <Trash2 size={18} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Floating Action Buttons */}
                        {customTemplatesData.length > 0 && (
                          <div className="sticky bottom-0 left-0 right-0 bg-shade-50 pt-6 pb-4 px-4 border-t border-rule">
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                              <button
                                onClick={() => setShowUseTemplateModal(true)}
                                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-accent rounded-ns  hover:shadow-pop transition-all duration-200 transform hover:bg-deep"
                              >
                                <SquarePen size={18} />
                                Use Existing
                              </button>
                              <button
                                onClick={() => {
                                  setShowCustomTemplate(true);
                                  setIsUpdating(false);
                                }}
                                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-accent rounded-ns  hover:shadow-pop transition-all duration-200 transform "
                              >
                                <Plus size={18} strokeWidth={3} />
                                Create New
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="bg-white border-2 border-rule border-t-0 rounded-b-ns px-6 py-4">
                    <button
                      type="button"
                      onClick={handleSubmitNewTemplates}
                      disabled={cannotGenerateNotes}
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-accent hover:bg-deep rounded-ns  hover:shadow-pop disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform  active:scale-95"
                    >
                      <span className={getButtonTextClass()}>
                        <StarsIcon />
                      </span>
                      <span className={getButtonTextClass()}>Write it up</span>
                    </button>

                    <p className="text-xs text-muted text-center mt-2">
                      <Star className="inline h-3 w-3 mb-0.5 mr-1" fill="var(--ns-warn)" stroke="none" />
                      Favourite templates are generated during your consults.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            </div>,
            document.body
          )}
        </>
      )}

      {showUseTemplateModal &&
        <UseExistingTemplateModal
          setShowUseTemplateModal={setShowUseTemplateModal}
          filteredAllTemplates={filteredAllTemplates}
          handlePreviewClick={handlePreviewClick}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          customTemplateUpdation={customTemplateUpdation}
        />}

      {/* Preview Modal */}
      {previewTemplate && (
        <>
          {ReactDOM.createPortal(
            <div className="fixed inset-0 bg-ink/50 backdrop-blur-sm z-[88] animate-fade-in" onClick={closePreview} />,
            document.body
          )}

          {ReactDOM.createPortal(
            <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none">
            <div
              className="pointer-events-auto w-[90vw] max-w-3xl max-h-[85vh] bg-white rounded-ns overflow-hidden shadow-pop animate-scale-in"
            >
              {/* Modal Header. Was `bg-accent` with `text-ink` type and a
                  `text-accent` icon on it — the same unreadable combination as
                  the Templates page preview, and the invisible icon is what made
                  the name look indented. */}
              <div className="flex items-center justify-between gap-4 border-b border-solid border-rule bg-white p-6">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent-tint">
                    <Sparkle className="h-4 w-4 text-accent" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="truncate font-display text-ui-lg font-medium text-ink">
                      {previewTemplate.name}
                    </h3>
                    <p className="font-body text-ui-xs text-muted">Template preview</p>
                  </div>
                </div>
                <CloseButton onClick={closePreview} label="Close preview" />
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="mb-6">
                  <p className="text-ink text-sm leading-relaxed">
                    {previewTemplate.description}
                  </p>

                  {/*{previewTemplate.rules && previewTemplate.rules.length !== 0 && (
                    <div className="mt-6 p-4 bg-shade-50 border border-rule rounded-ns">
                      <h4 className="font-semibold text-ink text-sm mb-2 flex items-center gap-2">
                        <div className="w-1 h-4 bg-accent rounded-full"></div>
                        Template Rules
                      </h4>
                      <p className="text-ink text-sm leading-relaxed">
                        {previewTemplate.rules}
                      </p>
                    </div>
                  )}*/}
                </div>

                {/* Topics */}
                <div>
                  <h4 className="font-semibold text-ink text-base mb-3 flex items-center gap-2">
                    <div className="w-1 h-5 bg-accent rounded-full"></div>
                    Template Topics
                  </h4>
                  <p className="text-ink text-sm mb-4">
                    Consultations are categorised and summarised under these topics.
                  </p>
                  {isPreviewLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-rule border-t-accent"></div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {previewTopics.map((topic, index) => (
                        <div
                          key={index}
                          className='bg-white border border-rule-strong rounded-ns hover:border-accent-light transition-all duration-200 overflow-hidden'
                        >
                          <button
                            onClick={() => setNotShowTemplateDescription(prev => {
                              let newIds = [...prev];
                              if (!newIds.includes(topic.id)) {
                                newIds.push(topic.id);
                              } else {
                                newIds = newIds.filter(newTopicId => newTopicId !== topic.id)
                              }
                              return newIds;
                            })}
                            className="w-full flex items-center justify-between p-4 hover:bg-shade-50 transition-colors"
                          >
                            <h5 className="font-medium text-ink text-sm text-left">
                              {topic.name || 'Untitled Topic'}
                            </h5>
                            {notShowTemplateDescription.includes(topic.id) ?
                              <ChevronDown className="h-5 w-5 text-ink" /> :
                              <ChevronUp className="h-5 w-5 text-ink" />
                            }
                          </button>

                          {!notShowTemplateDescription.includes(topic.id) && (
                            <div className='border-t border-rule p-4 bg-shade-50 text-sm text-ink leading-relaxed animate-slide-down'>
                              {topic.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end px-6 py-4 border-t border-rule bg-shade-50">
                <button
                  onClick={closePreview}
                  className="px-4 py-2 text-sm font-medium text-ink bg-danger/10 rounded-ns   transition-all duration-200 transform  hover:text-white"
                >
                  Close
                </button>
              </div>
            </div>
            </div>,
            document.body
          )}
        </>
      )}

      {/* Create/Edit Template Modal */}
      {showCustomTemplate && (
        <>
          {ReactDOM.createPortal(
            <div className="fixed inset-0 bg-ink/70 backdrop-blur-sm z-[89] animate-fade-in" />,
            document.body
          )}

          {ReactDOM.createPortal(
            <div className="fixed inset-0 z-[91] flex items-center justify-center p-4 pointer-events-none">
            <div
              className="pointer-events-auto w-[90vw] sm:w-[80vw] lg:w-[70vw] h-[90vh] max-w-[1000px] rounded-ns bg-white shadow-pop p-6 flex flex-col border border-rule animate-scale-in"
            >
              <div className="relative w-full flex flex-col h-full">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-rule">
                  <h2 className="font-display text-h5 font-medium text-ink">
                    {!isUpdating ? 'Create Custom Template' : 'Update Template'}
                  </h2>
                  {/* This was CloseIcon — a raster image of a WHITE cross, on a
                      white modal. Invisible, and no colour class could fix it. */}
                  <CloseButton onClick={closeModal} label="Close" />
                </div>

                <div className="flex flex-col gap-4 mb-4">
                  <div className="flex flex-col">
                    <InputFieldLabel isRequired={true} label="Template Name" className="mb-1.5 text-sm font-medium" />
                    <input
                      {...register("templateName", { required: "Template name is required" })}
                      placeholder="Enter template name..."
                      className="py-2 px-4 input-field-classes text-base border border-rule-strong rounded-ns focus:ring-2 focus:ring-accent focus:border-transparent"
                    />
                    {errors?.templateName && (
                      <span className="text-danger text-sm mt-1">{errors.templateName.message}</span>
                    )}
                  </div>

                  {showDetails && (
                    <>
                      <div className="flex flex-col">
                        <InputFieldLabel label="Description" className="mb-1.5 text-sm font-medium" />
                        <textarea
                          {...register("description")}
                          className="py-2 px-4 input-field-classes border border-rule-strong rounded-ns focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                          rows={3}
                          placeholder="Describe your template..."
                        />
                      </div>

                      <div className="flex flex-col">
                        <InputFieldLabel label="Rules" className="mb-1.5 text-sm font-medium" />
                        <textarea
                          {...register("rules")}
                          className="py-2 px-4 input-field-classes border border-rule-strong rounded-ns focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                          rows={3}
                          placeholder="Add any specific rules or guidelines..."
                        />
                      </div>
                    </>
                  )}

                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center gap-2 text-sm text-accent hover:text-accent font-medium self-start"
                  >
                    {showDetails ? (
                      <>
                        <ChevronUp size={16} />
                        Hide Details
                      </>
                    ) : (
                      <>
                        <ChevronDown size={16} />
                        Show Details
                      </>
                    )}
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto border border-rule rounded-ns p-4 bg-shade-50 space-y-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-ink">Topics</h3>
                    <button
                      type="button"
                      onClick={() => setExpandAllTopics(prev => !prev)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:text-accent"
                    >
                      {expandAllTopics ? (
                        <>
                          <ChevronUp size={16} />
                          Collapse all
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} />
                          Expand all
                        </>
                      )}
                    </button>
                  </div>

                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                      {fields.map((field, ind) => (
                        <SortableTopicRow
                          key={field.id}
                          field={field}
                          ind={ind}
                          register={register}
                          errors={errors}
                          getValues={getValues}
                          setValue={setValue}
                          fields={fields}
                          inputValues={inputValues}
                          handleRemoveTopic={handleRemoveTopic}
                          openDropdownIndex={openDropdownIndex}
                          setOpenDropdownIndex={setOpenDropdownIndex}
                          expanded={expandAllTopics}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>

                  {fields.length < 10 && (
                    <button
                      type="button"
                      onClick={handleAddTopics}
                      className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-rule-strong rounded-ns hover:border-accent-light hover:bg-shade-50 transition-all duration-200"
                    >
                      <PlusIcon className="h-5 w-5 text-muted" />
                      <span className="text-sm font-medium text-muted">Add Topic</span>
                    </button>
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-rule">
                  <button
                    onClick={handlePreviewButtonClick}
                    disabled={saveTemplateLoading || updateTemplateLoading}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-ink bg-shade-50 rounded-ns   transition-all duration-200 transform "
                  >
                    <Sparkle size={16} />
                    Preview
                  </button>

                  {!isUpdating ? (
                    <button
                      onClick={handleSubmit(handleSaveTemplate)}
                      disabled={saveTemplateLoading}
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-ink bg-shade-50 hover:bg-accent rounded-ns   transition-all duration-200 transform  disabled:opacity-50 disabled:cursor-not-allowed hover:text-white"
                    >
                      {saveTemplateLoading ? 'Saving...' : 'Save Template'}
                    </button>
                  ) : (
                    <button
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-ink bg-shade-50 hover:bg-accent rounded-ns   transition-all duration-200 transform  disabled:opacity-50 disabled:cursor-not-allowed hover:text-white"
                      onClick={handleSubmit(handleUpdateTemplate)}
                      disabled={updateTemplateLoading}
                    >
                      {updateTemplateLoading ? 'Updating...' : 'Update Template'}
                    </button>
                  )}
                </div>
              </div>
            </div>
            </div>,
            document.body
          )}
        </>
      )}

      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          closeModal={() => setShowDeleteModal(false)}
        >
          <DeleteConfirmation
            data={showDeleteModal}
            setShowDeleteModal={() => setShowDeleteModal(false)}
          />
        </Modal>
      )}

    </>
  );
};

export default AddMoreTemplates;